import React, { useCallback, useEffect, useRef, useState } from 'react'
import CheckinPageContainer from '../features/checkin/components/CheckinPageContainer'
import {
  Box,
  TextField,
  CircularProgress,
  Grid,
  Button,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import important from '../utils/important'
import { createLogger } from '../app/logger'
import CheckinSnackbar from '../features/snackbar/components/CheckinSnackbar'
import useAppDispatch from '../hooks/useAppDispatch'
import { useHistory } from 'react-router-dom'
import {
  useCreateAnonymousSessionMutation,
  usePostValidateCodeMutation,
} from '../api/ctliveApi'
import { useEventId } from '../app/location'
import {
  cacheToken,
  getAccessCodeSelector,
  setAccessCode,
  setUsername,
} from '../features/session/sessionSlice'
import useQuery from '../hooks/useQuery'
import useAppSelector from '../hooks/useAppSelector'

const logger = createLogger({
  prefix: 'AccessPage',
})

export const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& .MuiInput-input': {
      color: important('#00B0FF'),
      fontSize: '18px',
    },
    '& label': {
      fontSize: '18px',
    },
    '& .MuiFormLabel-asterisk': {
      color: important('#00B0FF'),
    },
  },
  nameField: {
    width: '100%',
  },
  nameContainer: {
    width: '45%',
    marginBottom: '20px',
  },
  accessCodeContainer: {
    width: '45%',
    marginBottom: '20px',
  },
  spinner: {
    marginTop: '10px',
  },
  accessCodeField: {
    width: '60%',
    height: 58,
    marginRight: '2vw',
    '& input': {
      fontWeight: 'bold',
      fontSize: '16px',
    },
  },
  accessCodeButtonsContainer: {
    marginTop: 30,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  loaderContainer: {
    justifyContent: 'center',
  },
}))

const ACCESS_CODE_REQUIRED_LENGTH = 6

const AccessPage = () => {
  const dispatch = useAppDispatch()
  const { classes } = useStyles()

  const query = useQuery()
  const history = useHistory()
  const eventId = useEventId()

  const accessCodeRef = useRef<HTMLInputElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)

  const [dirtyUsername, setDirtyUsername] = useState('')
  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const [isAccessCodePopulated, setIsAccessCodePopulated] = useState(false)
  const accessCode = useAppSelector(getAccessCodeSelector)

  const [postValidateCode, { isLoading: isLoadingPost }] =
    usePostValidateCodeMutation()
  const [createAnonymousSession, { isLoading: isLoadingCreateSession }] =
    useCreateAnonymousSessionMutation()

  const checkAccess = useCallback(
    async (accessCode: string) => {
      try {
        const { hasAccess } = await postValidateCode({
          code: +accessCode,
          eventId,
        }).unwrap()
        setHasAccess(hasAccess)
        return hasAccess
      } catch (err) {
        logger.error(err)
        setHasAccess(false)
        return false
      }
    },
    [eventId, postValidateCode]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (accessCode.length === ACCESS_CODE_REQUIRED_LENGTH) {
        if (e.key === 'Enter') {
          submitRef.current?.focus()
        } else if (e.key === 'Tab') {
          e.preventDefault()
          submitRef.current?.focus()
        }
      }
    },
    [accessCode]
  )

  const handleReset = useCallback(() => {
    const fromQs = query.accessCode
    if (fromQs) {
      delete query.accessCode
      history.push({
        search: new URLSearchParams(query).toString(),
      })
    }
    dispatch(setAccessCode(''))
    setHasAccess(false)
    setIsAccessCodePopulated(false)
    accessCodeRef.current?.focus()
  }, [accessCodeRef, history, query, dispatch])

  const handleAccessCodeChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.replace(/\D/g, '')
      dispatch(setAccessCode(val))

      if (val.length < ACCESS_CODE_REQUIRED_LENGTH) {
        setIsAccessCodePopulated(false)
        if (!val) {
          setHasAccess(null)
        }
      } else {
        setIsAccessCodePopulated(true)
        await checkAccess(val)
      }
    },
    [checkAccess, dispatch]
  )

  const handleSubmit = useCallback(async () => {
    if (hasAccess) {
      const { token } = await createAnonymousSession().unwrap()
      dispatch(cacheToken(token))
      dispatch(setUsername(dirtyUsername))
    } else {
      accessCodeRef.current?.focus()
    }
  }, [hasAccess, createAnonymousSession, dispatch, dirtyUsername])

  useEffect(() => {
    (async function () {
      const fromQs = window.atob(query.accessCode || '')
      if (fromQs?.length === ACCESS_CODE_REQUIRED_LENGTH) {
        dispatch(setAccessCode(fromQs))
        setIsAccessCodePopulated(true)
        await checkAccess(fromQs)
      }
    })()
  }, [checkAccess, query, dispatch])

  return (
    <CheckinPageContainer headerText='Check-In' topLevel={<CheckinSnackbar />}>
      <Grid
        container
        item
        xs={12}
        justifyContent='space-between'
        className={classes.container}
      >
        <Grid item xs={11} md={5} className={classes.nameContainer}>
          <TextField
            autoFocus={true}
            className={classes.nameField}
            inputProps={{ inputMode: 'text' }}
            label='Enter Your Name'
            onChange={(e) => setDirtyUsername(e.target.value)}
            onKeyDown={handleKeyDown}
            required
            value={dirtyUsername}
            variant='standard'
          />
        </Grid>
        <Grid item xs={12} md={6} className={classes.accessCodeContainer}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              className={classes.accessCodeField}
              error={hasAccess === false}
              helperText={hasAccess === false ? 'Incorrect Passcode' : ''}
              inputProps={{
                maxLength: ACCESS_CODE_REQUIRED_LENGTH,
                inputMode: 'numeric',
                pattern: '[0-9]{1,6}',
              }}
              inputRef={accessCodeRef}
              key='access-code'
              label='Enter your 6-Digit Code'
              onChange={handleAccessCodeChange}
              onKeyDown={handleKeyDown}
              required
              type='text'
              value={accessCode}
              variant='standard'
            />
            {(isLoadingPost || isLoadingCreateSession) && (
              <CircularProgress
                size={20}
                thickness={4}
                className={classes.spinner}
              />
            )}
          </Box>
          <Grid className={classes.accessCodeButtonsContainer}>
            <Button
              disabled={isAccessCodePopulated}
              onClick={handleReset}
              variant='text'
            >
              Reset
            </Button>
            <Button
              disabled={
                (dirtyUsername.length || 0) < 1 ||
                !hasAccess ||
                isLoadingPost ||
                isLoadingCreateSession
              }
              onClick={handleSubmit}
              ref={submitRef}
              variant='contained'
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </CheckinPageContainer>
  )
}

export default AccessPage
